/** Money is a string of cents on the wire ("27350.00") and stays a string
 * here. Nothing in the front end does arithmetic on money: the backend prices
 * every line and total, and a screen that recomputed them would be a second
 * source that can disagree. These helpers only format, compare and sum for
 * display -- and the sum is integer arithmetic on cents, never floats. */

/** "27350.00" -> "R27 350,00", the South African form the documents use. */
export function formatMoney(cents: string | number | null | undefined): string {
  if (cents === null || cents === undefined || cents === "") {
    return "—";
  }
  const parsed = toCents(cents);
  if (parsed === null) {
    return String(cents);
  }
  const sign = parsed < 0 ? "-" : "";
  const whole = Math.trunc(Math.abs(parsed) / 100);
  const fraction = Math.abs(parsed) % 100;
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${sign}R${grouped},${String(fraction).padStart(2, "0")}`;
}

/** A decimal string to an integer number of cents, or null when it is not a
 * number. Half-up on a third decimal, as the backend rounds. */
export function toCents(value: string | number): number | null {
  const text = String(value).trim().replace(/\s/g, "").replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(text)) {
    return null;
  }
  const negative = text.startsWith("-");
  const [whole, fraction = ""] = text.replace("-", "").split(".");
  const padded = (fraction + "000").slice(0, 3);
  let cents = Number(whole) * 100 + Number(padded.slice(0, 2));
  if (Number(padded[2]) >= 5) {
    cents += 1;
  }
  return negative ? -cents : cents;
}

/** Integer cents back to the wire's "27350.00". */
export function fromCents(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const magnitude = Math.abs(cents);
  return `${sign}${Math.trunc(magnitude / 100)}.${String(magnitude % 100).padStart(2, "0")}`;
}

/** The sum of decimal strings, exact, as a decimal string. */
export function sumMoney(values: Iterable<string | null | undefined>): string {
  let total = 0;
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }
    total += toCents(value) ?? 0;
  }
  return fromCents(total);
}

/** A quantity without trailing zeros: "8.000" -> "8", "104.500" -> "104,5". */
export function formatQuantity(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const text = String(value);
  if (!/^-?\d+(\.\d+)?$/.test(text)) {
    return text;
  }
  const [whole, fraction = ""] = text.split(".");
  const trimmed = fraction.replace(/0+$/, "");
  return trimmed === "" ? whole : `${whole},${trimmed}`;
}

/** "12.5" -> "12,5%"; "25" -> "25%". */
export function formatPercent(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return `${formatQuantity(value)}%`;
}

/** A rate as a fraction ("0.145") to a percentage ("14,5%"), exactly. The
 * decimal point moves two places in the string; `Number("0.145") * 100` is
 * 14.499999999999998, and a VAT or penalty line must not say so. */
export function formatRate(fraction: string | number | null | undefined): string {
  if (fraction === null || fraction === undefined || fraction === "") {
    return "—";
  }
  const text = String(fraction).trim();
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text);
  if (match === null) {
    return text;
  }
  const [, sign, whole, fractionDigits = ""] = match;
  const padded = fractionDigits.padEnd(2, "0");
  const shiftedWhole = `${whole}${padded.slice(0, 2)}`.replace(/^0+(?=\d)/, "");
  const rest = padded.slice(2);
  return formatPercent(rest === "" ? `${sign}${shiftedWhole}` : `${sign}${shiftedWhole}.${rest}`);
}
